import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getNodesBounds, getViewportForBounds, Panel, useReactFlow, useStore } from 'reactflow';
import { color, EL_COLORS } from '@/colors';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_AI_WEAVER } from '@/consts/featureFlags';
import { TOOLBAR_WIDTH_V2 } from '../consts/ui';
import { AIChatPanel } from './AIChatPanel';
import { useAIWeaverStore } from './aiWeaver.store';

const AI_PANEL_WIDTH = 320;
const LEFT_OFFSET = TOOLBAR_WIDTH_V2 + 8;
const DRAWER_SPACE = AI_PANEL_WIDTH + LEFT_OFFSET;

// Flag-check shell. Returns null before any subscription hooks so flag-off
// users pay zero runtime cost (no zustand / ReactFlow store subscriptions,
// no window listener, no effects).
export function AIWeaver() {
  const { value: isEnabled } = useFeatureFlag(FF_AI_WEAVER);
  if (!isEnabled) return null;
  return <AIWeaverImpl />;
}

function AIWeaverImpl() {
  const isOpen = useAIWeaverStore((s) => s.isOpen);
  const [hasMessages, setHasMessages] = useState(false);
  const clearFnRef = useRef<(() => void) | null>(null);
  const { fitView, getNodes, setViewport } = useReactFlow();
  const vpWidth = useStore((s) => s.width);
  const vpHeight = useStore((s) => s.height);

  const handleChatStateChange = useCallback((messages: boolean, clearFn: () => void) => {
    setHasMessages(messages);
    clearFnRef.current = clearFn;
  }, []);

  const fitViewAroundDrawer = useCallback(
    (duration = 400) => {
      const nodes = getNodes();
      if (nodes.length === 0 || !vpWidth || !vpHeight) {
        fitView({ duration, padding: 0.2 });
        return;
      }
      const leftPad = isOpen ? DRAWER_SPACE : 0;
      const bounds = getNodesBounds(nodes);
      const { x, y, zoom } = getViewportForBounds(bounds, vpWidth - leftPad, vpHeight, 0.5, 2, 0.2);
      setViewport({ x: x + leftPad, y, zoom }, { duration });
    },
    [fitView, getNodes, setViewport, isOpen, vpWidth, vpHeight],
  );

  // Refit when drawer toggles so content stays visible in the non-drawer area.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fitViewAroundDrawer(300);
    // fitViewAroundDrawer depends on isOpen; only react to isOpen changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Refit on AI-driven workflow changes, accounting for the drawer.
  useEffect(() => {
    const handler = () => fitViewAroundDrawer(400);
    window.addEventListener('ai-workflow-changed', handler);
    return () => window.removeEventListener('ai-workflow-changed', handler);
  }, [fitViewAroundDrawer]);

  if (!isOpen) return null;

  return (
    <Panel
      position="top-left"
      style={{
        height: '100%',
        width: `${AI_PANEL_WIDTH}px`,
        top: '0px',
        left: `${LEFT_OFFSET}px`,
        margin: 0,
      }}
      className="slide-left-enter"
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: color.Black92,
          borderRight: `1px solid ${EL_COLORS.BoxBorder}`,
          borderLeft: `1px solid ${EL_COLORS.BoxBorder}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${EL_COLORS.BoxBorder}`,
            px: 1.5,
            py: 1,
            gap: 1,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 14, color: color.White64_T }} />
          <Box sx={{ flex: 1, fontSize: 12, fontWeight: 600, color: color.White100 }}>AI Weaver</Box>
          {hasMessages && clearFnRef.current && (
            <IconButton
              onClick={() => clearFnRef.current?.()}
              size="small"
              sx={{ color: color.White40_T, p: 0.25 }}
              aria-label="Clear chat"
            >
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
        <AIChatPanel onChatStateChange={handleChatStateChange} />
      </Box>
    </Panel>
  );
}
